'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SiteCooperationsEditable } from "@/app/features/sites/cooperations/types/types";
import { AutosaveState } from "@/app/lib/hooks/useAutosave";
import { SiteCooperation, SiteCooperationInput } from "@/app/lib/generated/graphql-types";
import { SiteCooperationsSchema } from "@/app/features/sites/cooperations/types/schema";
import { getSiteCooperations } from "@/app/features/sites/cooperations/data-access/queries";
import { updateSiteCooperations } from "@/app/features/sites/cooperations/data-access/mutations";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";

type ChildEditorSnapshot = {
    hasChanges: boolean;
    autosaveState: AutosaveState | null;
};

type ChildEditorActions = {
    publish: () => Promise<boolean>;
    discardChanges: () => Promise<void>;
};

type SiteCooperationsEditorContextType = UseSingleTypeEditorType<SiteCooperationsEditable> & {
    registerChildEditor: (documentId: string, snapshot: ChildEditorSnapshot, actions: ChildEditorActions) => void;
    unregisterChildEditor: (documentId: string) => void;
};

const SiteCooperationsEditorContext = createContext<SiteCooperationsEditorContextType | undefined>(undefined);

export function useSiteCooperationsEditor() {
    const context = useContext(SiteCooperationsEditorContext);
    if (!context) {
        throw new Error("useSiteCooperationsEditor must be used within a SiteCooperationsEditor");
    }
    return context;
}

interface SiteCooperationsEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function SiteCooperationsEditorProvider({ children, isDraftModeEnabled }: SiteCooperationsEditorProviderProps) {
    const editor = useSingleTypeEditor<SiteCooperation, SiteCooperationsEditable, SiteCooperationInput>({
        schema: SiteCooperationsSchema,
        loadEntity: getSiteCooperations,
        update: updateSiteCooperations
    })

    return (
        <SiteCooperationsEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={() => {
                    editor.discardChanges();
                }}
            />
        </SiteCooperationsEditorContext.Provider>
    )
}
